import type { CartItem } from '../contexts/CartContext';

interface OrderEmailData {
  orderId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: CartItem[];
  address: {
    fullName: string;
    phone: string;
    email: string;
    houseNumber: string;
    landmark: string;
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    address: string;
  };
  scheduling?: {
    date: string;
    time: string;
  };
  payment: {
    method: string;
    subtotal: number;
    total: number;
  };
}

// Web3Forms API Configuration
const WEB3FORMS_ACCESS_KEY = '94f15d34-72ee-4a6f-9045-d1d1199f39c0';
const RAPSAPP_EMAIL = 'rapsappservice@gmail.com';

/**
 * EMAIL SERVICE - Using Web3Forms
 * - Reliable email delivery
 * - No SMTP configuration needed
 * - Admin notification: Sent to rapsappservice@gmail.com
 * - Customer confirmation: Sent directly to customer's email
 */

export const sendOrderEmails = async (orderData: OrderEmailData) => {
  try {
    console.log('Sending order confirmation emails via Web3Forms...');
    
    // Send email to RAPSAPP admin
    await sendAdminOrderNotification(orderData);
    
    // Send email directly to customer
    await sendCustomerOrderEmail(orderData);
    
    console.log('✅ Order emails sent successfully via Web3Forms');
    return { success: true, method: 'web3forms' };
  } catch (error) {
    console.error('❌ Web3Forms failed:', error);
    console.log('Email service error, but order was placed successfully');
    // Don't fail the order if email fails
    return { success: false, error, message: 'Order placed but email notification failed' };
  }
};

// Email to customer with order confirmation
const sendCustomerOrderEmail = async (orderData: OrderEmailData) => {
  const itemsList = orderData.items
    .map(item => {
      let itemText = `
      • ${item.name}
         - Professional: ${item.provider || 'To be assigned'}
         - Price: ₹${item.price}
         - Quantity: ${item.quantity}`;
      
      // Add addons if any
      if (item.addons && item.addons.length > 0) {
        itemText += '\n         Add-ons:';
        item.addons.forEach(addon => {
          itemText += `\n            ◦ ${addon.name} - ₹${addon.price}${addon.duration ? ' (' + addon.duration + ')' : ''}`;
        });
      }
      
      return itemText;
    })
    .join('\n');

  const message = `
Dear ${orderData.customerName},

Thank you for choosing RAPSAPP! Your order has been placed successfully.

ORDER DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Order ID: ${orderData.orderId}
Order Date: ${new Date().toLocaleDateString('en-IN', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
})}

SERVICES BOOKED:
${itemsList}

${orderData.scheduling ? `SCHEDULING:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Date: ${orderData.scheduling.date}
Time: ${orderData.scheduling.time}
` : ''}
${orderData.items.some((item: any) => item.scheduling) ? `SCHEDULED SERVICES:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${orderData.items.map((item: any, index: number) => 
  item.scheduling ? `${index + 1}. ${item.name} - ${item.scheduling.date} at ${item.scheduling.time}` : ''
).filter(Boolean).join('\n')}
` : ''}
SERVICE ADDRESS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${orderData.address.fullName}
${orderData.address.houseNumber ? orderData.address.houseNumber + ', ' : ''}${orderData.address.streetAddress}
${orderData.address.landmark ? 'Near ' + orderData.address.landmark + ', ' : ''}${orderData.address.city}
${orderData.address.state} - ${orderData.address.pincode}
Phone: ${orderData.address.phone}

PAYMENT SUMMARY:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Subtotal:      ₹${orderData.payment.subtotal.toFixed(2)}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TOTAL:         ₹${orderData.payment.total.toFixed(2)}
Payment Method: ${orderData.payment.method.toUpperCase()}

WHAT'S NEXT?
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1. You will receive a confirmation call within 2 hours
2. Our professional will arrive at the scheduled time
3. Service will be completed as per your requirements
4. Payment will be collected after service completion

TRACK YOUR ORDER:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
You can check your order status anytime by visiting:
My Account → My Bookings section on www.rapsapp.in

NEED HELP?
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Phone: +91 7018401540
Email: rapsappservice@gmail.com
Website: www.rapsapp.in

Thank you for trusting RAPSAPP for your home service needs!

Best regards,
Team RAPSAPP
Made with ❤️ in Apna Himachal
  `.trim();

  const response = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `Order Confirmation - ${orderData.orderId} - RAPSAPP`,
      from_name: 'RAPSAPP',
      name: orderData.customerName,
      email: orderData.customerEmail, // Send directly to customer's email
      message: message,
      replyto: 'rapsappservice@gmail.com'
      // Removed 'cc' field - customer gets their own email directly
    })
  });

  const result = await response.json();
  if (!result.success) {
    throw new Error('Failed to send customer email');
  }
  
  return result;
};

// Email to admin with new order notification
const sendAdminOrderNotification = async (orderData: OrderEmailData) => {
  const itemsList = orderData.items
    .map(item => {
      let itemTotal = item.price * item.quantity;
      let itemText = `
      • ${item.name}
         Professional: ${item.provider || 'To be assigned'}
         Price: ₹${item.price}
         Quantity: ${item.quantity}`;
      
      // Add addons if any
      if (item.addons && item.addons.length > 0) {
        itemText += '\n         Add-ons:';
        item.addons.forEach(addon => {
          itemText += `\n            ◦ ${addon.name} - ₹${addon.price}${addon.duration ? ' (' + addon.duration + ')' : ''}`;
          itemTotal += addon.price * item.quantity;
        });
      }
      
      itemText += `\n         Total: ₹${itemTotal}`;
      return itemText;
    })
    .join('\n');

  const message = `
🔔 NEW ORDER RECEIVED!

ORDER INFORMATION:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Order ID: ${orderData.orderId}
Order Time: ${new Date().toLocaleString('en-IN', { 
  dateStyle: 'full', 
  timeStyle: 'short' 
})}

CUSTOMER DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Name: ${orderData.customerName}
Email: ${orderData.customerEmail}
Phone: ${orderData.customerPhone}

SERVICES ORDERED:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${itemsList}

${orderData.scheduling ? `SCHEDULING:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Preferred Date: ${orderData.scheduling.date}
Preferred Time: ${orderData.scheduling.time}
` : ''}
${orderData.items.some((item: any) => item.scheduling) ? `SCHEDULED SERVICES:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${orderData.items.map((item: any, index: number) => 
  item.scheduling ? `${index + 1}. ${item.name} - ${item.scheduling.date} at ${item.scheduling.time}` : ''
).filter(Boolean).join('\n')}
` : ''}
SERVICE ADDRESS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${orderData.address.fullName}
${orderData.address.houseNumber ? orderData.address.houseNumber + ', ' : ''}${orderData.address.streetAddress}
${orderData.address.landmark ? 'Landmark: ' + orderData.address.landmark : ''}
${orderData.address.city}, ${orderData.address.state} - ${orderData.address.pincode}
Contact: ${orderData.address.phone}

PAYMENT DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Subtotal:      ₹${orderData.payment.subtotal.toFixed(2)}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TOTAL AMOUNT:  ₹${orderData.payment.total.toFixed(2)}
Payment Method: ${orderData.payment.method.toUpperCase()}
Status: PENDING

ACTION REQUIRED:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1. Confirm order with customer within 2 hours
2. Assign professional for the service
3. Ensure professional reaches on time
4. Follow up after service completion

---
This is an automated notification from RAPSAPP Order Management System
  `.trim();

  const response = await fetch('https://api.web3forms.com/submit', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `🔔 NEW ORDER: ${orderData.orderId} - ${orderData.customerName}`,
      from_name: 'RAPSAPP Order System',
      name: orderData.customerName,
      email: 'rapsappservice@gmail.com', // Send to admin email
      message: message,
      replyto: orderData.customerEmail // Reply goes to customer
    })
  });

  const result = await response.json();
  if (!result.success) {
    throw new Error('Failed to send admin notification email');
  }
  
  return result;
};
